import { createAction } from '@reduxjs/toolkit';
import type { components } from '../schema/docs/api/admin';
import type { AppThunk } from '../store/configureStore';
import { throwOnError } from './utils';

export type PrivateMoney = components['schemas']['PrivateMoney'];
type GetPrivateMoneyResponse = {
  id: string;
  organization: {
    code: string;
    name: string;
  };
  name: string;
  furigana: string;
  type: 'own' | 'third-party';
  can_use_c2c_transfer: boolean;
  expiration_type: 'static' | 'last-update' | 'last-topup-update';
  expires_in_months: number | null;
  expires_in_days: number | null;
  expires_at: string | null;
  point_expiration_type: 'static' | 'last-payment-update';
  point_expires_in_months: number | null;
  point_expires_in_days: number | null;
  point_expires_at: string | null;
  max_balance: number;
  transfer_limit: number;
  money_topup_transfer_limit: number;
  total_topup_limit: {
    type: 'entire-period' | 'range' | null;
    amount: number | null;
    started_at: string | null;
  } | null;
  legal_notes: {
    terms: string;
    privacy_policy: string;
    payment_act: string;
    commercial_act: string;
  };
  access_limitation:
    | 'unlimited'
    | 'exclusive'
    | 'hidden'
    | 'prohibit-add-account'
    | 'custom-application';
  display_money_and_point: 'both' | 'money-only' | 'point-only';
  unit: string;
  description: string;
  oneline_message: string;
  images: {
    '300x300': string | null;
    '600x600': string | null;
    card: string | null;
  } | null;
  sounds: {
    payment: string | null;
  } | null;

  can_use_credit_card: boolean;
  require_3ds_challenge?: boolean;
  topup_methods:
    | {
        type: string;
        amounts: number[];
        range: [number, number];
        additional_info?: {
          credit_card_daily_cap?: number | null;
          credit_card_monthly_cap?: number | null;
          issuer_code?: string;
          issuer_name?: string;
          money_name?: string;
          p_id?: string;
          iss_type?: string;
          store_code?: string;
          bankbook_merchant_name?: string;
        };
      }[]
    | null;
  is_topup_quota_available: boolean;
  kiosk_colors: {
    base_color: string;
    button_foreground_color: string;
    button_background_color: string;
  } | null;
  custom_domain_name: string | null;
  transaction_webhook_callback_url: string | null;
  itrust_tenant_code: string | null;
  resident_address_pattern: string | null;
  identification_validity_days: number | null;
  fallback_private_money_uuid: string | null;
};

type GetPrivateMoneyAccountStatisticsResponse = {
  private_money_id: string;
  total_amount_accounts: number;
  total_accounts_until_today: number;
};

export const SELECT_PRIVATE_MONEY = createAction<PrivateMoney>('SELECT_PRIVATE_MONEY');

export const getPrivateMoneys = (query = '') =>
  fetch(`/api/private-moneys?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<{ rows: PrivateMoney[] }>);

export const getPrivateMoney = (id: string) =>
  fetch(`/api/private-moneys/${id}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<GetPrivateMoneyResponse>);

// TODO: anti-pattern, should fix this
export const createPrivateMoney =
  (formData: RequestInit['body']): AppThunk<Promise<PrivateMoney>> =>
  () =>
    fetch('/api/private-moneys', {
      method: 'POST',
      credentials: 'same-origin',
      body: formData,
    }).then(throwOnError<PrivateMoney>);

export const updatePrivateMoney = (id: string, formData: RequestInit['body']) =>
  fetch(`/api/private-moneys/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    body: formData,
  }).then(throwOnError);

export const selectPrivateMoney =
  (money: PrivateMoney): AppThunk =>
  (dispatch) =>
    dispatch(SELECT_PRIVATE_MONEY(money));

export const getPrivateMoneyAccountStatistics = (id: string, query = '') =>
  fetch(`/api/private-moneys/${id}/account-statistics?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<GetPrivateMoneyAccountStatisticsResponse>);
